import {
    EventPeriodEnum,
    ExclusionTypeEnum,
    MatchSegmentEnum,
    MatchStatusEnum,
} from '../utils/api';

export type OvertimeUiMode = 'play' | 'penalty_shootout';

export type PeriodCount = 1 | 2 | 3 | 4;

export type TimingSnapshot = {
    periodCount: PeriodCount;
    periodLengthMinutes: number;
    shotClockMaxSeconds: number;
    shotClockResetSeconds: number;
    exclusionDurationSeconds: number;
    brutalityDurationSeconds: number;
};

export const DEFAULT_TIMING: TimingSnapshot = {
    periodCount: 4,
    periodLengthMinutes: 8,
    shotClockMaxSeconds: 28,
    shotClockResetSeconds: 18,
    exclusionDurationSeconds: 18,
    brutalityDurationSeconds: 240,
};

const REGULATION_PERIODS: EventPeriodEnum[] = [
    EventPeriodEnum.First_Period,
    EventPeriodEnum.Second_Period,
    EventPeriodEnum.Third_Period,
    EventPeriodEnum.Fourth_Period,
];

export type PeriodPill = {
    periodId: EventPeriodEnum;
    label: string;
    overtimeMode?: OvertimeUiMode;
};

export type MatchTimingFields = {
    matchSegmentId?: number;
    matchSegmentLength?: number;
    shotClockMaxSeconds?: number;
    shotClockResetSeconds?: number;
    exclusionDurationSeconds?: number;
    brutalityDurationSeconds?: number;
};

export type ExclusionClockEvent = {
    id: number;
    periodId: EventPeriodEnum;
    minute: number;
    seconds: number;
    typeId: ExclusionTypeEnum;
    excludedPlayerId?: number | null;
    excludedCoachId?: number | null;
    teamId?: number | null;
    isPenaltyExclusion?: boolean;
};

export type ActiveExclusionClock = {
    eventId: number;
    playerId: number;
    remainingSeconds: number;
    teamId?: number;
};

const asPeriodCount = (n: number): PeriodCount => {
    if (n === 1 || n === 2 || n === 3 || n === 4) return n;
    return DEFAULT_TIMING.periodCount;
};

export const periodCountFromSegment = (segmentId: number): PeriodCount => {
    switch (segmentId) {
        case MatchSegmentEnum.Single:
            return 1;
        case MatchSegmentEnum.Half:
            return 2;
        case MatchSegmentEnum.Third:
            return 3;
        case MatchSegmentEnum.Quarter:
            return 4;
        default:
            return DEFAULT_TIMING.periodCount;
    }
};

export const segmentFromPeriodCount = (count: PeriodCount): MatchSegmentEnum => {
    switch (count) {
        case 1:
            return MatchSegmentEnum.Single;
        case 2:
            return MatchSegmentEnum.Half;
        case 3:
            return MatchSegmentEnum.Third;
        default:
            return MatchSegmentEnum.Quarter;
    }
};

export const regulationPeriodIds = (periodCount: PeriodCount): EventPeriodEnum[] =>
    REGULATION_PERIODS.slice(0, periodCount);

export const lastRegulationPeriodId = (periodCount: PeriodCount): EventPeriodEnum =>
    REGULATION_PERIODS[periodCount - 1];

const regulationLabel = (index: number, periodCount: PeriodCount): string => {
    if (periodCount === 1) return '1';
    if (periodCount === 2) return `H${index + 1}`;
    if (periodCount === 3) return `P${index + 1}`;
    return `Q${index + 1}`;
};

export const periodLabel = (
    periodId: EventPeriodEnum,
    periodCount: PeriodCount,
    overtimeMode: OvertimeUiMode = 'play'
): string => {
    if (periodId === EventPeriodEnum.Overtime) {
        return overtimeMode === 'penalty_shootout' ? 'PSO' : 'OT';
    }
    const index = REGULATION_PERIODS.indexOf(periodId);
    if (index < 0) return '';
    return regulationLabel(index, periodCount);
};

export const periodPills = (periodCount: PeriodCount): PeriodPill[] => {
    const regulation = regulationPeriodIds(periodCount).map((periodId, index) => ({
        periodId,
        label: regulationLabel(index, periodCount),
    }));
    return [
        ...regulation,
        { periodId: EventPeriodEnum.Overtime, label: 'OT', overtimeMode: 'play' },
        {
            periodId: EventPeriodEnum.Overtime,
            label: 'PSO',
            overtimeMode: 'penalty_shootout',
        },
    ];
};

export const gameClockInitialSeconds = (periodLengthMinutes: number): number =>
    periodLengthMinutes * 60;

export const clampShotClock = (seconds: number, gameRemaining: number, max: number): number =>
    Math.max(0, Math.min(max, Math.min(seconds, gameRemaining)));

export const applyShotClockFoulBump = (
    current: number,
    reset: number,
    gameRemaining: number,
    max: number
): number => clampShotClock(current < reset ? reset : current, gameRemaining, max);

export const elapsedFromMatchStart = (
    periodId: EventPeriodEnum,
    minute: number,
    seconds: number,
    periodLengthMinutes: number,
    periodCount: PeriodCount
): number => {
    const periodSeconds = gameClockInitialSeconds(periodLengthMinutes);
    const remaining = minute * 60 + seconds;
    const elapsedIntoPeriod = periodSeconds - remaining;
    const regulationIndex = REGULATION_PERIODS.indexOf(periodId);
    const order =
        periodId === EventPeriodEnum.Overtime
            ? periodCount
            : regulationIndex >= 0
            ? regulationIndex
            : 0;
    return order * periodSeconds + elapsedIntoPeriod;
};

export const canEditMatchTiming = (_statusId: MatchStatusEnum): boolean => false;

const positiveOrDefault = (value: number | undefined, fallback: number): number =>
    value != null && value > 0 ? value : fallback;

export const timingSnapshotFromMatch = (match?: MatchTimingFields | null): TimingSnapshot => ({
    periodCount: periodCountFromSegment(
        match?.matchSegmentId ?? segmentFromPeriodCount(DEFAULT_TIMING.periodCount)
    ),
    periodLengthMinutes: positiveOrDefault(
        match?.matchSegmentLength,
        DEFAULT_TIMING.periodLengthMinutes
    ),
    shotClockMaxSeconds: positiveOrDefault(
        match?.shotClockMaxSeconds,
        DEFAULT_TIMING.shotClockMaxSeconds
    ),
    shotClockResetSeconds: positiveOrDefault(
        match?.shotClockResetSeconds,
        DEFAULT_TIMING.shotClockResetSeconds
    ),
    exclusionDurationSeconds: positiveOrDefault(
        match?.exclusionDurationSeconds,
        DEFAULT_TIMING.exclusionDurationSeconds
    ),
    brutalityDurationSeconds: positiveOrDefault(
        match?.brutalityDurationSeconds,
        DEFAULT_TIMING.brutalityDurationSeconds
    ),
});

export const exclusionClockDurationSeconds = (
    typeId: ExclusionTypeEnum,
    timing: TimingSnapshot
): number | undefined => {
    // Penalty / 5 m: player stays in, no sit-out clock.
    if (typeId === ExclusionTypeEnum.Penalty) return undefined;
    if (typeId === ExclusionTypeEnum.Brutality) return timing.brutalityDurationSeconds;
    if (typeId === ExclusionTypeEnum.Exclusion || typeId === ExclusionTypeEnum.Suspension) {
        return timing.exclusionDurationSeconds;
    }
    return undefined;
};

/** Remaining sit-out seconds, or undefined if this Event never gets a clock. */
const exclusionRemainingSeconds = (
    event: ExclusionClockEvent,
    nowElapsedSeconds: number,
    timing: TimingSnapshot
): number | undefined => {
    if (!event.excludedPlayerId || event.excludedCoachId) return undefined;
    if (event.isPenaltyExclusion) return undefined;
    const duration = exclusionClockDurationSeconds(event.typeId, timing);
    if (duration == null) return undefined;
    const eventElapsed = elapsedFromMatchStart(
        event.periodId,
        event.minute,
        event.seconds,
        timing.periodLengthMinutes,
        timing.periodCount
    );
    return duration - (nowElapsedSeconds - eventElapsed);
};

export const activeExclusionClocks = (
    events: ExclusionClockEvent[],
    nowElapsedSeconds: number,
    timing: TimingSnapshot,
    dismissedEventIds: ReadonlySet<number>
): ActiveExclusionClock[] => {
    const clocks: ActiveExclusionClock[] = [];
    for (const event of events) {
        if (dismissedEventIds.has(event.id)) continue;
        const remaining = exclusionRemainingSeconds(event, nowElapsedSeconds, timing);
        if (remaining == null || remaining <= 0) continue;
        clocks.push({
            eventId: event.id,
            playerId: event.excludedPlayerId!,
            remainingSeconds: remaining,
            ...(event.teamId != null ? { teamId: event.teamId } : {}),
        });
    }
    return clocks;
};

/** Sit-outs that already reached 0s. Pin these so a Game clock rewind does not revive them. */
export const expiredExclusionEventIds = (
    events: ExclusionClockEvent[],
    nowElapsedSeconds: number,
    timing: TimingSnapshot,
    dismissedEventIds: ReadonlySet<number>
): number[] => {
    const ids: number[] = [];
    for (const event of events) {
        if (dismissedEventIds.has(event.id)) continue;
        const remaining = exclusionRemainingSeconds(event, nowElapsedSeconds, timing);
        if (remaining != null && remaining <= 0) ids.push(event.id);
    }
    return ids;
};

export const isRegulationPeriodAllowed = (
    periodId: EventPeriodEnum,
    periodCount: PeriodCount
): boolean =>
    periodId === EventPeriodEnum.Overtime || regulationPeriodIds(periodCount).includes(periodId);

/** Period count for forms: coerce Select value. */
export const parsePeriodCount = (value: unknown): PeriodCount => asPeriodCount(Number(value));

export const PERIOD_COUNT_OPTIONS: Array<{ value: PeriodCount; label: string }> = [
    { value: 1, label: '1' },
    { value: 2, label: '2' },
    { value: 3, label: '3' },
    { value: 4, label: '4' },
];

export type TimingFormValues = {
    periodCount: PeriodCount;
    matchSegmentLength: number;
    shotClockMaxSeconds: number;
    shotClockResetSeconds: number;
    exclusionDurationSeconds: number;
    brutalityDurationMinutes: number;
};

export const timingFormValuesFromFields = (fields?: MatchTimingFields | null): TimingFormValues => {
    const timing = timingSnapshotFromMatch(fields);
    return {
        periodCount: timing.periodCount,
        matchSegmentLength: timing.periodLengthMinutes,
        shotClockMaxSeconds: timing.shotClockMaxSeconds,
        shotClockResetSeconds: timing.shotClockResetSeconds,
        exclusionDurationSeconds: timing.exclusionDurationSeconds,
        brutalityDurationMinutes: timing.brutalityDurationSeconds / 60,
    };
};

export const timingPayloadFromForm = (values: {
    periodCount?: unknown;
    matchSegmentLength?: unknown;
    shotClockMaxSeconds?: unknown;
    shotClockResetSeconds?: unknown;
    exclusionDurationSeconds?: unknown;
    brutalityDurationMinutes?: unknown;
}): Required<MatchTimingFields> => ({
    matchSegmentId: segmentFromPeriodCount(parsePeriodCount(values.periodCount)),
    matchSegmentLength: Number(values.matchSegmentLength),
    shotClockMaxSeconds: Number(values.shotClockMaxSeconds),
    shotClockResetSeconds: Number(values.shotClockResetSeconds),
    exclusionDurationSeconds: Number(values.exclusionDurationSeconds),
    brutalityDurationSeconds: Math.round(Number(values.brutalityDurationMinutes) * 60),
});

const GOAL_SLOTS: Array<{
    home: 'homeTeamGoalsQ1' | 'homeTeamGoalsQ2' | 'homeTeamGoalsQ3' | 'homeTeamGoalsQ4';
    away: 'awayTeamGoalsQ1' | 'awayTeamGoalsQ2' | 'awayTeamGoalsQ3' | 'awayTeamGoalsQ4';
}> = [
    { home: 'homeTeamGoalsQ1', away: 'awayTeamGoalsQ1' },
    { home: 'homeTeamGoalsQ2', away: 'awayTeamGoalsQ2' },
    { home: 'homeTeamGoalsQ3', away: 'awayTeamGoalsQ3' },
    { home: 'homeTeamGoalsQ4', away: 'awayTeamGoalsQ4' },
];

export type GoalColumn = {
    label: string;
    home?: number | null;
    away?: number | null;
};

export const regulationGoalColumns = (
    match: Partial<
        Record<
            (typeof GOAL_SLOTS)[number]['home'] | (typeof GOAL_SLOTS)[number]['away'],
            number | null | undefined
        >
    >,
    periodCount: PeriodCount
): GoalColumn[] =>
    regulationPeriodIds(periodCount).map((periodId, index) => ({
        label: periodLabel(periodId, periodCount),
        home: match[GOAL_SLOTS[index].home],
        away: match[GOAL_SLOTS[index].away],
    }));
